import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical } from "lucide-react";
import Dropdown from "./ui/Dropdown";
import { useAllBusiness } from "../services/queries/queries";
import Pagination from "./ui/Pagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

function BusinessTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("");
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      if (globalFilter !== debouncedFilter) {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter, debouncedFilter]);

  const handleViewDetails = (business) => {
    navigate(`/business/${business.uuid}`, { state: { business } });
  };

  const { data, isError, isLoading, error } = useAllBusiness(
    page,
    limit,
    status,
    debouncedFilter.trim() || undefined
  );
  const navigate = useNavigate();

  const columns = [
    {
      accessorFn: (row) => row.business?.name || row.tradingName || "N/A",
      header: "Business Name",
    },
    {
      accessorFn: (row) => row.legalName || "N/A",
      header: "Legal Name",
    },
    {
      accessorFn: (row) => row.business?.email || "N/A",
      header: "Email",
    },
    {
      accessorFn: (row) => row.business?.country || "N/A",
      header: "Country",
    },
    {
      accessorFn: (row) => row.business?.phoneNumber || "N/A",
      header: "Phone Number",
    },
    {
      accessorFn: (row) => row.status || "Not Submitted",
      header: "KYC Status",
      cell: (info) => {
        const status = info.getValue();
        const statusStyles = {
          "Approved": "bg-green-100 text-green-800",
          "Pending": "bg-yellow-100 text-yellow-800",
          "Rejected": "bg-red-100 text-red-800",
          "Not Submitted": "bg-gray-100 text-gray-800",
          "In Review": "bg-blue-100 text-blue-800"
        };

        return (
          <span
            className={`px-3 py-3 rounded-xl text-sm font-medium ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const business = row.original;
        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(business),
          }
        ];

        // Add approve option if not already approved
        if (business.kycStatus !== "Approved") {
          dropdownItems.push({
            label: "Approve KYC",
            onClick: () => openApproveModal(business),
            className: "text-green-600",
          });
        }

        // Add decline option if not already rejected
        if (business.kycStatus !== "Rejected") {
          dropdownItems.push({
            label: "Decline KYC",
            onClick: () => openDeclineModal(business),
            className: "text-red-600",
          });
        }

        return (
          <Dropdown
            trigger={<MoreVertical className="h-4 w-4" />}
            items={dropdownItems}
          />
        );
      },
    },
  ];

  const openApproveModal = (business) => {
    setSelectedBusiness(business);
    setIsApproveModalOpen(true);
  };

  const openDeclineModal = (business) => {
    setSelectedBusiness(business);
    setIsDeclineModalOpen(true);
  };

  const approveKYC = () => {
    setIsApproveModalOpen(false);
    
    try {
      // In a real implementation, you would send this to an API
      toast.success("KYC request approved successfully");
      // Refresh data or update locally
    } catch (error) {
      toast.error("Failed to approve KYC request");
      console.error("Error approving KYC:", error);
    }
    
    setSelectedBusiness(null);
  };

  const declineKYC = () => {
    setIsDeclineModalOpen(false);
    
    try {
      // In a real implementation, you would send this to an API
      toast.success("KYC request declined successfully");
      // Refresh data or update locally
    } catch (error) {
      toast.error("Failed to decline KYC request");
      console.error("Error declining KYC:", error);
    }
    
    setSelectedBusiness(null);
  };

  const tableData = data?.data || [];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: data?.meta?.totalPages || 0,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Loading business data...</p>
        </div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Businesses</h1>
      </div>

      <div className="flex justify-between mb-6">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search"
            className="w-full pl-10 pr-4 py-3 border rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2 md:px-6 md:py-4 text-left text-sm font-medium text-gray-500 bg-[#F2F2F7]"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 md:px-6 md:py-7 text-sm text-[#333333] font-sans"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data?.meta && data.meta.totalPages > 1 && (
        <div className="p-6 border-t border-gray-200 bg-gray-50/50 mt-6">
          <Pagination
            currentPage={page}
            totalPages={data.meta.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Approve Modal */}
      {isApproveModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-[90%] max-w-md">
            <h3 className="text-lg font-medium mb-4">Approve KYC</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to approve this business's KYC? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-full"
                onClick={() => setIsApproveModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-full"
                onClick={approveKYC}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {isDeclineModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-md">
            <h3 className="text-lg font-medium mb-4">Decline KYC</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to decline this business's KYC? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-full"
                onClick={() => setIsDeclineModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-full"
                onClick={declineKYC}
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessTable;